/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */
import * as authredux from "../../src/app/modules/Auth/_redux/authRedux";
import { cekToken, refreshToken } from "../../src/app/modules/Auth/_redux/authCrud";
import { AuthPage, Logout } from "./modules/Auth";
import { Redirect, Route, Switch } from "react-router-dom";
import { shallowEqual, useSelector, useDispatch } from "react-redux";

import BasePage from "./BasePage";
import ErrorsPage from "./modules/ErrorsExamples/ErrorsPage";
import { Layout, LayoutSplashScreen } from "../_metronic/layout";
import React, { useEffect, useState } from "react";
import ChangePassword from "./modules/Auth/pages/ChangePassword";

const SESSION_CHECK_MS = 150000;

export function Routes() {

    const { isAuthorized, isExpired, token, user } = useSelector(
        ({ auth }) => ({
            isAuthorized: auth.authToken ? true : false,
            isExpired: auth.isExpired,
            token: auth.authToken,
            user: auth.user,
        }),
        shallowEqual
    );

    const dispatch = useDispatch();
    const keyMultiApp = user?.key_multi_app;
    const [initializingSession, setInitializingSession] = useState(Boolean(token));

    useEffect(() => {
        if (!token || !keyMultiApp) {
            setInitializingSession(false);
            return;
        }

        let cancelled = false;
        let checking = false;

        const expireSession = status => {
            if (!cancelled) {
                dispatch(authredux.actions.cekTokenAuth({ statusToken: status, isExpired: true }));
            }
        };

        const tryRefreshToken = async () => {
            try {
                const { data } = await refreshToken(keyMultiApp);
                if (!cancelled) dispatch(authredux.actions.refreshTokenAuth(data));
            } catch (error) {
                const status = error.response?.status;
                if (status === 401 || status === 403) expireSession(status);
            }
        };

        const validateSession = async () => {
            if (checking) return;
            checking = true;
            try {
                const { data, status } = await cekToken(token);
                if (data.isexpired || data.ttl < 300) {
                    await tryRefreshToken();
                } else if (!cancelled) {
                    dispatch(authredux.actions.cekTokenAuth({ statusToken: status, isExpired: false }));
                }
            } catch (error) {
                const status = error.response?.status;
                if (status === 401 || status === 403) await tryRefreshToken();
            } finally {
                checking = false;
                if (!cancelled) setInitializingSession(false);
            }
        };

        const checkWhenVisible = () => {
            if (document.visibilityState === "visible") validateSession();
        };

        validateSession();
        const interval = setInterval(validateSession, SESSION_CHECK_MS);
        window.addEventListener("focus", validateSession);
        document.addEventListener("visibilitychange", checkWhenVisible);

        return () => {
            cancelled = true;
            clearInterval(interval);
            window.removeEventListener("focus", validateSession);
            document.removeEventListener("visibilitychange", checkWhenVisible);
        };
    }, [dispatch, keyMultiApp, token]);

    if (initializingSession) return <LayoutSplashScreen />;

    return (
        <Switch>

            {!isAuthorized || isExpired || !user ? (
                (
                    <Route>
                        <AuthPage />
                    </Route>
                )
            ) :
                (<Redirect from="/auth" to="/" />)}

            <Route path="/error" component={ErrorsPage} />
            <Route path="/logout" component={Logout} />

            {isAuthorized && !isExpired && user && user.password_update_required && (
                <Route>
                    <ChangePassword />
                </Route>
            )}

            <Layout>
                <BasePage />
            </Layout>
        </Switch>
    );
}
